import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, ShoppingCart, Trash2, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Wishlist = () => {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const { addToCart } = useCart();
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login?redirect=/wishlist');
      return;
    }
    fetchWishlist();
  }, [isAuthenticated, navigate]);

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(response.data);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    setActionLoading(prev => ({ ...prev, [productId]: 'removing' }));
    try {
      await axios.delete(`${API}/wishlist/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(prev => prev.filter(item => item.product_id !== productId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove from wishlist');
    } finally {
      setActionLoading(prev => ({ ...prev, [productId]: null }));
    }
  };

  const handleMoveToCart = async (item) => {
    const product = item.product;
    const variantSize = item.variant_size || product.variants[0]?.size;
    
    if (!variantSize) {
      toast.error('Please select a variant');
      return;
    }

    setActionLoading(prev => ({ ...prev, [item.product_id]: 'cart' }));
    
    try {
      const response = await axios.post(
        `${API}/wishlist/move-to-cart/${item.product_id}?variant_size=${encodeURIComponent(variantSize)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add to cart using the returned cart item
      addToCart(response.data.cart_item);
      setWishlist(prev => prev.filter(w => w.product_id !== item.product_id));
      toast.success('Added to cart!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setActionLoading(prev => ({ ...prev, [item.product_id]: null }));
    }
  };

  if (loading) {
    return (
      <div className="section-spacing">
        <div className="container-custom">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-spacing" data-testid="wishlist-page">
      <div className="container-custom">
        {/* Header */}
        <div className="mb-8">
          <Link to="/products" className="inline-flex items-center text-muted-foreground hover:text-primary mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Continue Shopping
          </Link>
          <div className="flex items-center gap-3">
            <Heart className="w-8 h-8 text-primary" />
            <h1 className="text-4xl font-heading font-bold">My Wishlist</h1>
          </div>
          <p className="text-muted-foreground mt-2">
            {wishlist.length} {wishlist.length === 1 ? 'item' : 'items'} saved
          </p>
        </div>

        {wishlist.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-card">
            <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2">Your wishlist is empty</h2>
            <p className="text-muted-foreground mb-6">Save items you love by clicking the heart icon on products</p>
            <Link to="/products">
              <Button className="btn-primary">Browse Products</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wishlist.map((item) => {
              const product = item.product;
              if (!product) return null;
              
              const minPrice = Math.min(...product.variants.map(v => v.price));
              const maxPrice = Math.max(...product.variants.map(v => v.price));
              const isLoading = actionLoading[item.product_id];
              
              return (
                <div 
                  key={item.id} 
                  className="bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-hover transition-all"
                  data-testid={`wishlist-item-${item.product_id}`}
                >
                  {/* Product Image */}
                  <Link to={`/products/${product.id}`}>
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={product.images?.[0] || '/placeholder.png'}
                        alt={product.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </Link>
                  
                  {/* Product Info */}
                  <div className="p-6">
                    <Link to={`/products/${product.id}`}>
                      <h3 className="text-lg font-heading font-semibold hover:text-primary transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                      {product.description}
                    </p>
                    
                    {/* Price */}
                    <div className="mt-3">
                      {minPrice === maxPrice ? (
                        <span className="text-xl font-bold text-primary">₹{minPrice}</span>
                      ) : (
                        <span className="text-xl font-bold text-primary">
                          ₹{minPrice} - ₹{maxPrice}
                        </span>
                      )}
                    </div>
                    
                    {/* Variant Info */}
                    {item.variant_size && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Selected: {item.variant_size}
                      </p>
                    )}
                    
                    {/* Actions */}
                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={() => handleMoveToCart(item)}
                        disabled={isLoading}
                        className="flex-1 btn-primary"
                        data-testid={`add-to-cart-${item.product_id}`}
                      >
                        {isLoading === 'cart' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Add to Cart
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleRemoveFromWishlist(item.product_id)}
                        disabled={isLoading}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`remove-wishlist-${item.product_id}`}
                      >
                        {isLoading === 'removing' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
